import { Badge } from '@/components/badge';
import { Card } from '@/components/card';
import OpenInNewIcon from '@/components/icon/OpenInNewIcon';
import LinearLoading from '@/components/linear-loading';
import { Link } from '@/components/link';
import { useSnackbar } from '@/contexts/SnackBarContext';
import { useUpdateAlertMutation } from '@/hooks/suba/useUpdateAlertMutation.hook';
import { DisbursementRequestAlertDto } from '@/models/suba/alerts/disbursement-request-alert-dto.model';
import {
  DisbursementDto,
  DisbursementReviewStatus
} from '@/models/suba/disbursements/DisbursementDTO.model';
import { executionStatusColorMap } from '@/models/suba/workflows/ExecutionStatusColorMap.model';
import { FeatureLevelPermissions } from '@/models/UserPermissions.model';
import { useAlertContext } from '@/routes/suba/common/contexts/AlertContext';
import SubAccountService from '@/services/suba/accounts/SubAccount.service';
import { DisbursementService } from '@/services/suba/disbursement/Disbursement.service';
import WorkflowService from '@/services/suba/workflows/Workflow.service';
import { userService } from '@/services/User.service';
import formatters from '@/utils/Formatters';
import {
  Alert,
  Button,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToggle } from '@vestwell-frontend/hooks';
import { AlertStatus } from '@vestwell-sub-accounting/models/common/AlertStatus';

import { AxiosError } from 'axios';
import { FC } from 'react';

export const DisbursementActions: FC = () => {
  const alert = useAlertContext<DisbursementRequestAlertDto>();

  const queryClient = useQueryClient();

  const { showSnackbar } = useSnackbar();
  const [isApproveDialogOpen, toggleIsApproveDialogOpen] = useToggle(false);
  const [isDenyDialogOpen, toggleIsDenyDialogOpen] = useToggle(false);

  const getDisbursementQuery = useQuery(
    ['DisbursementService.getById', alert.details?.disbursementRequestQueueId],
    () => DisbursementService.getById(alert.details.disbursementRequestQueueId),
    {
      enabled: !!alert.details?.disbursementRequestQueueId
    }
  );
  const updateAlertMutation = useUpdateAlertMutation();

  const reviewMutation = useMutation({
    mutationFn: (reviewedStatus: DisbursementReviewStatus) => {
      const userId = userService.getUser()?.sub;

      if (!userId) {
        showSnackbar({
          message: `Unable to get logged in user for approving/disapproving disbursement`,
          severity: 'error'
        });
        return;
      }

      return DisbursementService.review(
        alert.details?.disbursementRequestQueueId,
        {
          alertId: alert?.id,
          reviewedAt: new Date().toISOString(),
          reviewedBy: userId,
          reviewedStatus
        }
      );
    },
    mutationKey: [
      'DisbursementService.review',
      alert.details?.disbursementRequestQueueId
    ],
    onError: (error: AxiosError<string>) => {
      showSnackbar({
        message: error.response?.data ?? error.message,
        severity: 'error'
      });
    },
    onSuccess: () => {
      showSnackbar({
        message: 'Disbursement request reviewed successfully',
        severity: 'success'
      });
      queryClient.invalidateQueries([
        'DisbursementService.getById',
        alert.details?.disbursementRequestQueueId
      ]);
    }
  });

  const getWorkflowQuery = useQuery(
    ['WorkflowService.getById', alert.details?.tracerId],
    async () => WorkflowService.getById(`tracer:${alert.details.tracerId}`),
    {
      enabled: !!alert.details?.tracerId
    }
  );

  const getSubaccountQuery = useQuery(
    ['SubAccountService.getById', getDisbursementQuery.data?.fromSubAccountId],
    async () =>
      SubAccountService.getById(getDisbursementQuery.data.fromSubAccountId),
    {
      enabled: !!getDisbursementQuery.data?.fromSubAccountId
    }
  );

  return (
    <>
      <Card>
        <CardContent>
          <Stack data-testid='actions-card' spacing={1}>
            {reviewMutation.isLoading && <LinearLoading />}
            <Stack
              alignItems='center'
              direction='row'
              justifyContent='space-between'
              spacing={2}>
              <Typography variant='h6'>Available actions</Typography>
              <Stack alignItems='center' direction='row' spacing={2}>
                {getSubaccountQuery.data?.investorId && (
                  <Button
                    endIcon={<OpenInNewIcon />}
                    href={`/participant/${getSubaccountQuery.data.investorId}/disbursements`}
                    size='small'
                    target='_blank'>
                    View Withdrawal Page
                  </Button>
                )}
                <Button
                  color='error'
                  data-testid='actions-deny-button'
                  disabled={
                    alert?.alertStatus === AlertStatus.Closed ||
                    (getDisbursementQuery.data &&
                      getDisbursementQuery.data.reviewedStatus !== null) ||
                    !userService.hasPermission(
                      FeatureLevelPermissions.WRITE_SUBA_DISBURSEMENTS_APPROVE
                    )
                  }
                  onClick={() => toggleIsDenyDialogOpen(true)}
                  variant='contained'>
                  Deny
                </Button>
                <Button
                  color='primary'
                  data-testid='actions-approve-button'
                  disabled={
                    alert?.alertStatus === AlertStatus.Closed ||
                    (getDisbursementQuery.data &&
                      getDisbursementQuery.data.reviewedStatus !== null) ||
                    !userService.hasPermission(
                      FeatureLevelPermissions.WRITE_SUBA_DISBURSEMENTS_APPROVE
                    )
                  }
                  onClick={() => toggleIsApproveDialogOpen(true)}
                  variant='contained'>
                  Approve
                </Button>
              </Stack>
            </Stack>

            {getDisbursementQuery.data &&
              getDisbursementQuery.data.reviewedStatus !== null && (
                <Alert
                  action={
                    <Button
                      onClick={() => {
                        updateAlertMutation.mutate({
                          alertId: alert.id,
                          updateRequest: {
                            alertStatus: AlertStatus.Closed
                          }
                        });
                      }}
                      size='small'>
                      Close Ticket
                    </Button>
                  }
                  data-testid='status-alert'
                  icon={false}
                  severity='info'
                  variant='outlined'>
                  <Stack spacing={1}>
                    {getDisbursementQuery.data.reviewedStatus !== null && (
                      <Typography variant='body2'>
                        Transfer has been{' '}
                        {getDisbursementQuery.data.reviewedStatus ===
                        DisbursementReviewStatus.approved
                          ? 'approved'
                          : 'denied'}
                      </Typography>
                    )}
                    {alert.details?.tracerId && getWorkflowQuery.data && (
                      <Stack direction='row' spacing={1}>
                        <Link
                          maxWidth={350}
                          target='_blank'
                          to={`/ops/workflows/tracer:${alert.details.tracerId}`}>
                          Tracer ID: {alert.tracerId}
                        </Link>
                        <Badge
                          color={
                            executionStatusColorMap[
                              getWorkflowQuery.data.executionStatus
                            ]
                          }
                          maxWidth='fit-content'>
                          {formatters.displayCase(
                            formatters.snakeToCamelCase(
                              getWorkflowQuery.data.executionStatus
                            )
                          )}
                        </Badge>
                      </Stack>
                    )}
                  </Stack>
                </Alert>
              )}
          </Stack>
        </CardContent>
      </Card>
      <ApproveDialog
        onApprove={() => {
          toggleIsApproveDialogOpen();
          reviewMutation.mutate(DisbursementReviewStatus.approved);
        }}
        onCancel={() => toggleIsApproveDialogOpen()}
        open={isApproveDialogOpen}
      />
      <DenyDialog
        disbusement={getDisbursementQuery.data}
        onCancel={() => toggleIsDenyDialogOpen()}
        onDeny={() => {
          toggleIsDenyDialogOpen();
          reviewMutation.mutate(DisbursementReviewStatus.rejected);
        }}
        open={isDenyDialogOpen}
      />
    </>
  );
};

type DisbursementApproveDialogProps = {
  onApprove: () => void;
  onCancel: () => void;
} & DialogProps;

const ApproveDialog = (props: DisbursementApproveDialogProps) => {
  return (
    <Dialog fullWidth maxWidth='xs' onClose={props.onCancel} open={props.open}>
      <DialogTitle>Approve request?</DialogTitle>
      <DialogContent />
      <DialogActions>
        <Button
          color='primary'
          data-testid='approve-dialog-cancel-button'
          onClick={props.onCancel}>
          Cancel
        </Button>
        <Button
          color='primary'
          data-testid='approve-dialog-approve-button'
          onClick={props.onApprove}
          variant='contained'>
          Approve
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type DisbursementDenyDialogProps = {
  onDeny: () => void;
  onCancel: () => void;
  disbusement: DisbursementDto;
} & DialogProps;

const DenyDialog = (props: DisbursementDenyDialogProps) => {
  return (
    <Dialog fullWidth maxWidth='xs' onClose={props.onCancel} open={props.open}>
      <DialogTitle>Deny request?</DialogTitle>
      <DialogContent>
        {!props.disbusement?.reason && (
          <Typography variant='body2'>
            You are not able to deny this request because the disbursement does
            not have a note. please add a note to the disbursement before
            denying it.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          color='primary'
          data-testid='deny-dialog-cancel-button'
          onClick={props.onCancel}>
          Cancel
        </Button>
        <Button
          color='error'
          data-testid='deny-dialog-deny-button'
          disabled={!props.disbusement?.reason}
          onClick={props.onDeny}
          variant='contained'>
          Deny
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DisbursementActions.displayName = 'DisbursementActions';
